import React, { useEffect, useState } from "react";
import zoomSdk from "@zoom/appssdk";
import Box from "@amzn/meridian/esm/components/box";
import Column from "@amzn/meridian/esm/components/column";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Link from "@amzn/meridian/esm/components/link";
import Theme from "@amzn/meridian/esm/components/theme/theme";
import summaryStopped from "../../images/summary_stop.svg";
import noSummary from "../../images/red_no_summary.svg";
import { AIMS_STATUS } from "../../constants";

import LogManager from "../utils/LogManager";

/**
 * Renders an attendee-facing summary-stopped view based on AiMS status.
 * Used when summarizing is stopped and attendees see either no summary or need to contact the organizer.
 *
 * @param {Object} props
 * @param {string} props.aimsStatus - One of AIMS_STATUS.NOT_AVAILABLE | SUMMARY_TO_ORGANIZER
 */
const AttendeeSummarizeStopped = ({ aimsStatus: initAimsStatus }) => {
  const [aimsStatus, setAimsStatus] = useState(initAimsStatus);

  useEffect(() => {
    if (window.location.href === "http://localhost:3000/") {
      console.log("[Localhost] setSummaryMessage as NOT_AVAILABLE");
      setAimsStatus(AIMS_STATUS.AVAILABLE);
    }
  }, []);

  if(aimsStatus === AIMS_STATUS.AVAILABLE) return <StoppedWithSummary />
  if (aimsStatus === AIMS_STATUS.NOT_AVAILABLE) return <StoppedNoSummary />;
  if (aimsStatus === AIMS_STATUS.SUMMARY_TO_ORGANIZER) return <StoppedToOrganizerOnly />;
  return null;
};

const StoppedWithSummary = () => (
  <StoppedTemplate
    image={summaryStopped}
    alt="summaryStopped"
    header="Summarizing meeting stopped"
    message=""
    //TODO: Switch to use the message once this issue fixed: https://sim.amazon.com/issues/MEETEX-169856
    // message="You’ll receive a meeting summary when it’s ready."
  />
);

const StoppedToOrganizerOnly = () => (
  <StoppedTemplate
    image={summaryStopped}
    alt="summaryStopped"
    header="Summarizing meeting stopped"
    message="Contact meeting organizer if you want to see the meeting summary."
  />
);

const openPolicyClick = async (url) => {
  console.log("trying to open amazon policy url", url);
  try {
    await zoomSdk.openUrl({ url: url });
  } catch (error) {
    LogManager.error("Failed to open amazon policy:", error);
  }
};

const StoppedNoSummary = () => (
  <StoppedTemplate
    image={noSummary}
    alt="noSummary"
    header="No meeting summary"
    message={
      <>
        There is no meeting summary because the meeting topics are not approved for Amazon Meetings Summary.
        <br />
        <br />
        Review{" "}
        <Link type="primary" onClick={openPolicyClick} style={{ color: "#008060" }}>
          Amazon policy
        </Link>{" "}
        for meeting recordings, transcriptions, and AI summarization.
      </>
    }
  />
);

const StoppedTemplate = ({ image, alt, header, message }) => (
  <Column alignmentHorizontal="start" width="100%" position="relative" spacing="none" spacingInset="none">
    <Column height="72px" />
    <Row spacing="none" spacingInset="none">
      <Theme tokens={{ boxBorderColorOutline: "#FFFFFF" }}>
        <Box type="outline" spacingInset="none" minWidth="15px" />
      </Theme>
      <Box width="340px" position="relative">
        <img src={image} alt={alt} style={{ display: "block", margin: "auto", maxWidth: "100%", height: "auto" }} />
      </Box>
    </Row>
    <Column height="16px" spacing="none" />
    <Column spacing="none">
      <Row spacing="none" spacingInset="none">
        <Theme tokens={{ boxBorderColorOutline: "#FFFFFF" }}>
          <Box type="outline" spacingInset="none" minWidth="68px" />
        </Theme>
        <Text fontFamily="amazonEmber" type="h50" alignment="start">
          {header}
        </Text>
      </Row>
    </Column>
    <Column height="16px" spacing="none" />
    <Column spacing="none" spacingInset="none">
      <Row spacing="none" spacingInset="none">
        <Theme tokens={{ boxBorderColorOutline: "#FFFFFF" }}>
          <Box type="outline" spacingInset="none" minWidth="68px" />
        </Theme>
        <Theme tokens={{ boxBorderColorOutline: "#FFFFFF" }}>
          <Box type="outline" spacingInset="none" maxWidth="239px">
            <Theme tokens={{ textListSpacingVertical: "160px" }}>
              <Text fontFamily="amazonEmber" type="b200" alignment="start" color="secondary">
                {message}
              </Text>
            </Theme>
          </Box>
        </Theme>
      </Row>
    </Column>
  </Column>
);

export default AttendeeSummarizeStopped;
